/** 회원가입/프로필 수정 등에서 공통 사용하는 폼 유효성 검사 */

export interface ValidationResult {
  isValid: boolean;
  message: string;
}

/** 비밀번호: 영문, 숫자, 특수문자(!@#$%^&*) 포함 8자 이상 */
export const validatePassword = (password: string): ValidationResult => {
  const regex = /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,}$/;
  if (!password) return { isValid: false, message: '' };
  if (!regex.test(password)) {
    return { isValid: false, message: '영문, 숫자, 특수문자 포함 8자 이상' };
  }
  return { isValid: true, message: '사용가능한 비밀번호 입니다.' };
};

/** 학번: 숫자만, 6자 이상 12자 이하 */
export const validateStudentNumber = (value: string): ValidationResult => {
  const numeric = value.replace(/[^0-9]/g, '');
  if (!numeric) return { isValid: false, message: '' };
  if (numeric.length < 6 || numeric.length > 12) {
    return { isValid: false, message: '학번은 6~12자리로 입력해주세요.' };
  }
  return { isValid: true, message: '올바른 형식입니다.' };
};

/** 휴대폰: 010-0000-0000 형식 (11자리 숫자) */
export const validatePhone = (value: string): ValidationResult => {
  const digits = value.replace(/-/g, '');
  if (!value) return { isValid: false, message: '' };
  if (!/^\d+$/.test(digits)) {
    return { isValid: false, message: '휴대폰 번호는 숫자만 입력 가능합니다.' };
  }
  if (digits.length !== 11) {
    return { isValid: false, message: '올바른 휴대폰 번호를 입력해주세요. (010-0000-0000)' };
  }
  if (!digits.startsWith('010')) {
    return { isValid: false, message: '010으로 시작하는 번호를 입력해주세요.' };
  }
  return { isValid: true, message: '올바른 형식입니다.' };
};

/** 이메일 형식 검증: 영문, 숫자, 허용된 특수문자만 가능 (한글 불가) */
export const validateEmail = (value: string): ValidationResult => {
  if (!value) return { isValid: false, message: '' };
  const trimmed = value.trim();
  const koreanRegex = /[ㄱ-ㅎㅏ-ㅣ가-힣]/;
  if (koreanRegex.test(trimmed)) {
    return { isValid: false, message: '이메일에 한글을 사용할 수 없습니다.' };
  }
  const validCharsRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
  if (!validCharsRegex.test(trimmed)) {
    return { isValid: false, message: '올바른 이메일 형식이 아닙니다.' };
  }
  return { isValid: true, message: '올바른 형식입니다.' };
};

/** 이메일 입력 시 한글 및 허용되지 않는 문자 제거 */
export const formatEmailInput = (value: string): string => {
  return value.replace(/[ㄱ-ㅎㅏ-ㅣ가-힣\s]/g, '');
};

/** 이름 유효성 검증: 한글 완성형 또는 영문만 허용 (자음/모음만, 특수문자, 숫자, 띄어쓰기 불허) */
export const validateName = (value: string): ValidationResult => {
  if (!value) return { isValid: false, message: '' };
  const incompleteKoreanRegex = /[ㄱ-ㅎㅏ-ㅣ]/;
  if (incompleteKoreanRegex.test(value)) {
    return { isValid: false, message: '완성되지 않은 한글(자음/모음만)은 사용할 수 없습니다.' };
  }
  if (/\s/.test(value)) {
    return { isValid: false, message: '이름에 띄어쓰기를 포함할 수 없습니다.' };
  }
  if (/[0-9]/.test(value)) {
    return { isValid: false, message: '이름에 숫자를 포함할 수 없습니다.' };
  }
  const validNameRegex = /^[가-힣a-zA-Z]+$/;
  if (!validNameRegex.test(value)) {
    return { isValid: false, message: '이름에 특수문자를 포함할 수 없습니다.' };
  }
  if (value.length < 2) {
    return { isValid: false, message: '이름은 2자 이상 입력해주세요.' };
  }
  return { isValid: true, message: '' };
};

/** 휴대폰 입력 시 010-0000-0000 자동 포맷팅 */
export const formatPhoneInput = (value: string): string => {
  const numeric = value.replace(/[^0-9]/g, '');
  if (numeric.length <= 3) return numeric;
  if (numeric.length <= 7) return `${numeric.slice(0, 3)}-${numeric.slice(3)}`;
  return `${numeric.slice(0, 3)}-${numeric.slice(3, 7)}-${numeric.slice(7, 11)}`;
};

/** 학번 입력 시 숫자만 허용 */
export const formatStudentNumberInput = (value: string): string => {
  return value.replace(/[^0-9]/g, '');
};

/** API 에러 메시지를 사용자 친화적인 메시지로 변환 */
export const getReadableErrorMessage = (error: any, defaultMessage: string = '오류가 발생했습니다.'): string => {
  const errorCode = error?.response?.data?.error || error?.response?.data?.code || '';
  const errorDetail = error?.response?.data?.detail || '';
  const statusCode = error?.response?.status;

  const errorMessages: Record<string, string> = {
    'USER_NOT_FOUND': '입력하신 정보와 일치하는 사용자를 찾을 수 없습니다.',
    'EMAIL_NOT_MATCH': '입력하신 이메일이 등록된 정보와 일치하지 않습니다.',
    'EMAIL_NOT_FOUND': '입력하신 이메일로 등록된 사용자를 찾을 수 없습니다.',
    'LOGIN_ID_NOT_FOUND': '입력하신 아이디로 등록된 사용자를 찾을 수 없습니다.',
    'INVALID_CREDENTIALS': '입력하신 정보가 올바르지 않습니다.',
    'EMAIL_ALREADY_USED': '이미 사용중인 이메일입니다.',
    'LOGIN_ID_ALREADY_USED': '이미 사용중인 아이디입니다.',
    'INVALID_CODE': '인증코드가 올바르지 않습니다.',
    'CODE_EXPIRED': '인증코드가 만료되었습니다. 다시 요청해주세요.',
    'TOO_MANY_REQUESTS': '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.',
    'NOT_FOUND': '요청하신 정보를 찾을 수 없습니다.',
  };

  if (errorCode && errorMessages[errorCode]) {
    return errorMessages[errorCode];
  }

  if (typeof errorCode === 'string' && errorCode.length > 0) {
    if (errorCode.includes('not found') || errorCode.includes('NOT_FOUND')) {
      return '입력하신 정보와 일치하는 사용자를 찾을 수 없습니다.';
    }
    if (errorCode.includes('Authentication') || errorCode.includes('535')) {
      return '이메일 발송 서버 설정 오류가 발생했습니다. 관리자에게 문의해주세요.';
    }
  }

  if (statusCode === 404) {
    return '입력하신 정보와 일치하는 사용자를 찾을 수 없습니다.';
  }
  if (statusCode === 400) {
    return typeof errorDetail === 'string' ? errorDetail : '입력하신 정보를 다시 확인해주세요.';
  }
  if (statusCode === 500) {
    return '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
  }

  if (typeof errorCode === 'string' && errorCode.length > 0) {
    return errorCode;
  }

  return defaultMessage;
};
